import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Checkbox, Alert, Divider, message } from 'antd';
import { UserOutlined, LockOutlined, GithubOutlined, GoogleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../../store';
import { login } from '../../store/slices/authSlice';
import { AuthService } from '../../services/auth';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [error, setError] = useState<string>('');

  // 管理员"切换到商户"：从URL hash读取签发的令牌直接登录
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#impersonate=')) {
      return;
    }
    try {
      const params = new URLSearchParams(hash.slice(1));
      const token = params.get('impersonate');
      const refreshToken = params.get('refresh');
      const userJson = params.get('user');
      if (token) {
        localStorage.setItem('token', token);
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }
        if (userJson) {
          localStorage.setItem('user', userJson);
        }
        // 清除hash避免令牌残留在地址栏/历史记录
        window.history.replaceState(null, '', window.location.pathname);
        message.success(t('auth.impersonateSuccess'));
        window.location.href = '/dashboard';
      }
    } catch (e) {
      // hash格式异常时走正常登录流程
    }
  }, []);

  const handleSubmit = async (values: LoginForm) => {
    try {
      setError('');
      const result = await dispatch(login({
        email: values.email,
        password: values.password,
      }));
      
      if (login.fulfilled.match(result)) {
        message.success(t('auth.loginSuccess'));
        navigate('/dashboard');
      } else {
        setError(result.payload as string || t('auth.loginFailed'));
      }
    } catch (err: any) {
      setError(err.message || t('auth.loginRetry'));
    }
  };

  const handleOAuthLogin = (provider: string) => {
    window.location.href = AuthService.getOAuthUrl(provider);
  };

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="auth-brand-logo">
          <span className="auth-brand-logo-icon">★</span>
          <span>FastStar</span>
        </div>
        <div className="auth-brand-copy">
          <h2>{t('auth.brandHeadline')}</h2>
          <p>{t('auth.brandSubLogin')}</p>
        </div>
        <div className="auth-brand-footer">{t('auth.brandFooter')}</div>
      </div>

      <div className="auth-form-side">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">Fast<span className="logo-accent">Star</span></div>
          <h1 className="auth-title">{t('auth.loginTitle')}</h1>
          <p className="auth-subtitle">{t('auth.loginSubtitle')}</p>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          className="auth-form"
          name="login"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('auth.emailRequired') },
              { type: 'email', message: t('auth.emailInvalid') }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('auth.email')}
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('auth.passwordRequired') },
              { min: 6, message: t('auth.passwordMin6') }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.password')}
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>{t('auth.rememberMe')}</Checkbox>
              </Form.Item>
              <Link to="/forgot-password">{t('auth.forgotPassword')}</Link>
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="auth-submit-btn"
            >
              {t('auth.login')}
            </Button>
          </Form.Item>
        </Form>

        <Divider className="auth-divider">{t('auth.or')}</Divider>

        <div className="oauth-buttons">
          <Button
            icon={<GoogleOutlined />}
            onClick={() => handleOAuthLogin('google')}
            className="oauth-btn"
          >
            Google
          </Button>
          <Button
            icon={<GithubOutlined />}
            onClick={() => handleOAuthLogin('github')}
            className="oauth-btn"
          >
            GitHub
          </Button>
        </div>

        <div className="auth-links">
          <span>{t('auth.noAccount')}</span>
          <Link to="/register">{t('auth.registerNow')}</Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginPage; 